/* eslint-disable @typescript-eslint/no-explicit-any */

import { cascadeEval } from 'utils/conditions';

type SortFn = (a: any, b: any) => number;

const SORT_RESULTS = {
  up: -1,
  down: 1,
  noChange: 0,
};

/**
 * Helper function to actively indicate which sort outcome you want when comparing 2 entries
 *
 * This is relevant because when comparing a x b wanting a certain outcome, their
 * result is virtually opposite:
 *
 * - if you want a to be before b: `-1`
 * - if you want b to be before a: `1`
 *
 * Because the reference is always the 'a' element, this is important
 *
 * If both are numbers, its easy to understand `a - b`
 * results in the same desired outcome exemplified above,
 * so no need for a special helper
 */
export function sortResult(target: 'a' | 'b', action: keyof typeof SORT_RESULTS) {
  const multiplier = target === 'a' ? 1 : -1;

  return multiplier * SORT_RESULTS[action];
}

export function randomizeArray<E = any>(array: E[]): E[] {
  return array.slice().sort(() => Math.random() - 0.5);
}

export function compareStrings(a: string, b: string): number {
  return a.localeCompare(b);
}

export function compareByName<E extends { name: string }>(entityA: E, entityB: E): number {
  return compareStrings(entityA.name, entityB.name);
}

export function sortByName<E extends { name: string }>(array: E[]): E[] {
  return array.slice().sort(compareByName);
}

export function evalSortResult(results: number[]): number {
  const [first, ...rest] = results;

  if (first || !rest.length) return first ?? 0;

  return evalSortResult(rest);
}

export function bestEffortSortCompare(a: any, b: any): number {
  const numeric = [a, b].every((item) =>
    ['number', 'boolean'].some((type) => typeof item === type),
  );

  if (numeric) return Number(b) - Number(a);

  return compareStrings(`${a}`, `${b}`);
}

export function bestEffortSort<Entity>(items: Entity[]): Entity[] {
  return items.slice().sort(bestEffortSortCompare);
}

function genericSorter(a: any, b: any): number {
  const numeric = [a, b].every((item) =>
    ['number', 'boolean'].some((type) => typeof item === type),
  );

  if (numeric) return Number(a) - Number(b);

  return compareStrings(`${a}`, `${b}`);
}

export function swapPosition<T>(list: T[], index1: number, index2: number): T[] {
  const newOrder = list.slice();

  const [removed] = newOrder.splice(index1, 1);
  newOrder.splice(index2, 0, removed);

  return newOrder;
}

export function sortByProperty<E>(
  array: E[],
  toSortProps: Extract<keyof E, string> | Array<Extract<keyof E, string>>,
  customSorters: { [key in keyof E]?: 'DESC' | 'ASC' | ((a: E[key], b: E[key]) => number) } = {},
): E[] {
  const props = Array.isArray(toSortProps) ? toSortProps : [toSortProps];

  return array.slice().sort((a, b) =>
    evalSortResult(
      props.map((prop) =>
        cascadeEval([
          {
            is: customSorters[prop] === 'ASC',
            then: () => genericSorter(a[prop], b[prop]),
          },
          {
            is: customSorters[prop] === 'DESC',
            then: () => genericSorter(b[prop], a[prop]),
          },
          {
            is: typeof customSorters[prop] === 'function',
            then: () => (customSorters[prop] as SortFn)?.(a[prop], b[prop]),
          },
          {
            is: true,
            then: () => compareStrings(`${a[prop]}`, `${b[prop]}`),
          },
        ]),
      ),
    ),
  );
}
